import React from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea } from 'recharts';

function TimeSeriesPopup({ segment, dataType, roadCode, thresholds, onClose, positionStyle }) {
  if (!segment || !segment.data) return null;

  const { seg_id, data, road_name, dir, start_km, end_km } = segment;

  // data は [{yymm, rsm, tv}] の配列
  // yymmからラベル生成
  const formatMonth = (yymm) => {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const s = String(yymm);
    const yy = parseInt(s.slice(0, 2), 10);
    const mm = parseInt(s.slice(2), 10);
    return `${monthNames[mm - 1]} ${2000 + yy}`;
  };

  // チャートデータを整形。Trafficは×10000で表示
  const chartData = [...data]
    .sort((a, b) => parseInt(a.yymm) - parseInt(b.yymm))
    .map(d => ({
      month: formatMonth(d.yymm),
      value: dataType === 'traffic' ? d.tv * 10000 : d.rsm
    }));

  const typeLabel = dataType === 'roughness' ? 'Road Roughness' : 'Traffic Volume';

  // road_codeごとの閾値を取得。Trafficは×10000で表示
  let threshold1, threshold2;
  if (thresholds && thresholds[roadCode] && thresholds[roadCode][dataType]) {
    [threshold1, threshold2] = thresholds[roadCode][dataType];
    if (dataType === 'traffic') {
      threshold1 = threshold1 * 10000;
      threshold2 = threshold2 * 10000;
    }
  } else {
    threshold1 = dataType === 'roughness' ? 2000 : 0.5;
    threshold2 = dataType === 'roughness' ? 4000 : 1.0;
  }

  const values = chartData.map(d => d.value);
  const maxValue = values.length > 0 ? Math.max(...values) : 0;
  const yAxisMin = 0;

  let yAxisMax;
  const baseMax = Math.max(maxValue, threshold2);
  const adjustedMax = baseMax * 1.2;
  if (dataType === 'roughness') {
    yAxisMax = adjustedMax <= 5000 ? 5000 : Math.ceil(adjustedMax / 1000) * 1000;
  } else {
    if (adjustedMax <= 20) yAxisMax = 20;
    else if (adjustedMax <= 100) yAxisMax = Math.ceil(adjustedMax / 10) * 10;
    else if (adjustedMax <= 500) yAxisMax = Math.ceil(adjustedMax / 50) * 50;
    else yAxisMax = Math.ceil(adjustedMax / 100) * 100;
  }

  const getColorByValue = (value) => {
    if (value >= threshold2) return '#EF4444';
    if (value >= threshold1) return '#F59E0B';
    return '#10B981';
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#ffffff',
        border: '1px solid #cbd5e1',
        borderRadius: '12px',
        padding: '20px',
        zIndex: 1000,
        minWidth: '400px',
        maxWidth: '500px',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.05)',
        ...positionStyle,
      }}
    >
      {/* ヘッダー */}
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h3 style={{ margin: 0, color: '#1e293b', fontSize: '16px', fontWeight: '600' }}>
            {typeLabel} Trend
            {dataType === 'traffic' && (
              <span style={{ fontSize: '11px', color: '#64748b', fontWeight: '400', marginLeft: '8px' }}>
                (×10000)
              </span>
            )}
          </h3>
          {road_name && (
            <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '12px' }}>{road_name}</p>
          )}
          <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: '12px' }}>
            Segment ID: {seg_id}
          </p>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#64748b',
            fontSize: '24px',
            cursor: 'pointer',
            padding: '0',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '4px',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
            e.target.style.color = '#2563eb';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'transparent';
            e.target.style.color = '#64748b';
          }}
        >
          ×
        </button>
      </div>

      {/* セグメント情報 */}
      {(dir || start_km != null) && (
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr',
          gap: '8px', padding: '10px 12px',
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px', marginBottom: '14px'
        }}>
          {dir && (
            <div>
              <p style={{ margin: 0, color: '#64748b', fontSize: '11px' }}>Direction</p>
              <p style={{ margin: '2px 0 0', color: '#1e293b', fontSize: '13px', fontWeight: '600' }}>{dir}</p>
            </div>
          )}
          {start_km != null && end_km != null && (() => {
            const sMin = Math.min(start_km, end_km);
            const sMax = Math.max(start_km, end_km);
            const latestEntry = chartData.length > 0 ? chartData[chartData.length - 1] : null;
            const latestVal = latestEntry?.value ?? null;
            const latestMonth = latestEntry?.month ?? null;
            const statusColor = latestVal == null ? '#64748b' : latestVal >= threshold2 ? '#EF4444' : latestVal >= threshold1 ? '#F59E0B' : '#10B981';
            const statusLabel = latestVal == null ? '-' : latestVal >= threshold2 ? 'Poor' : latestVal >= threshold1 ? 'Fair' : 'Good';
            return (
              <>
                <div>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '11px' }}>Section</p>
                  <p style={{ margin: '2px 0 0', color: '#1e293b', fontSize: '13px', fontWeight: '600' }}>{sMin.toFixed(1)} ～ {sMax.toFixed(1)} km</p>
                </div>
                <div>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '11px' }}>Latest Status</p>
                  <p style={{ margin: '2px 0 0', fontSize: '13px', fontWeight: '600', color: statusColor }}>
                    ● {statusLabel}
                    {latestVal != null && (
                      <span style={{ fontWeight: '600', color: '#1e293b', fontSize: '13px', marginLeft: '6px' }}>
                        {Math.round(latestVal).toLocaleString()} ({latestMonth})
                      </span>
                    )}
                  </p>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* グラフ */}
      <div style={{ marginBottom: '16px' }}>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData} margin={{ top: 30, right: 50, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <ReferenceArea y1={yAxisMin} y2={threshold1} fill="#10B981" fillOpacity={0.2} />
            <ReferenceArea y1={threshold1} y2={threshold2} fill="#F59E0B" fillOpacity={0.2} />
            <ReferenceArea y1={threshold2} y2={yAxisMax} fill="#EF4444" fillOpacity={0.2} />
            <XAxis dataKey="month" stroke="#64748b" style={{ fontSize: '12px' }} />
            <YAxis stroke="#64748b" style={{ fontSize: '12px' }} domain={[yAxisMin, yAxisMax]} />
            <Tooltip
              contentStyle={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                color: '#1e293b'
              }}
              labelStyle={{ color: '#64748b' }}
              formatter={(value) => Math.round(value)}
            />
            <Line
              type="monotone"
              dataKey="value"
              stroke="#334155"
              strokeWidth={2}
              dot={(props) => {
                const { cx, cy, payload } = props;
                return <circle cx={cx} cy={cy} r={5} fill={getColorByValue(payload.value)} stroke="white" strokeWidth={1.5} />;
              }}
              activeDot={(props) => {
                const { cx, cy, payload } = props;
                return <circle cx={cx} cy={cy} r={7} fill={getColorByValue(payload.value)} stroke="white" strokeWidth={1.5} />;
              }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default TimeSeriesPopup;